import {
	selectAllSelfAccountIds,
	selectAllUserContacts,
	selectContactById,
	selectUnarchivedContactEntities,
} from "@somewear/asset";
import { selectActiveIdentityId } from "@somewear/auth";
import { selectAllFiles } from "@somewear/files";
import type {
	ConversationPreview,
	IConversationBaseEntity,
	IDirectConversationEntity,
	MessageOrFile,
	MessagingState,
} from "@somewear/messaging";
import {
	countUnreadMessages,
	getMessageTimestamp,
	isFile,
	isNewConversation,
	isWorkspaceConversation,
	messageHasTimestamp,
	NEW_CONVERSATION_ID,
	selectAllConversations,
	selectAllMessages,
	selectConversationByKey,
	selectConversationEntities,
} from "@somewear/messaging";
import type { IContactForDisplay } from "@somewear/model";
import { PERSONAL_WORKSPACE_ID } from "@somewear/workspace";
import { workspaceInfoSelector } from "@web/app/appSelectors";
import type { RootState } from "@web/app/rootReducer";
import { createDeepEqualSelector } from "@web/common/utils";
import groupBy from "lodash/groupBy";
import moment from "moment";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import Config from "../config/Config";
import { collapseSosMessages } from "./sosMessageCollapse";

/**
const messagesSelector = (
	state: RootState,
	props: ConversationOrWorkspace
): DataMap<MessageResponseWithState> => {
	if (props.conversationId) {
		return state.messaging.messages[props.conversationId];
	} else {
		return state.messaging.workspaceMessages[props.workspaceId!];
	}
};

export const sortMessagesAsc = createSelector([messagesSelector], (messages) => {
	if (messages === undefined) return [];
	return Object.values(messages.data).sort((a, b) => {
		let aSeconds = a.timestamp!.seconds;
		let bSeconds = b.timestamp!.seconds;
		return aSeconds - bSeconds;
	});
});
 **/

export const selectNewConversationRecipient = (state: MessagingState) => {
	return state.messaging.recipient;
};

export const selectAllMessagesDeduped = createSelector(selectAllMessages, collapseSosMessages);

export const selectAllMessagesAndFiles = createSelector(
	selectAllMessagesDeduped,
	selectAllFiles,
	(messages, files) => {
		const messagesAndFiles = [...messages, ...files];

		return messagesAndFiles.sort((a, b) => {
			return getMessageTimestamp(a).seconds - getMessageTimestamp(b).seconds;
		});
	},
);

const readTimestampsSelector = (state: MessagingState) => {
	return state.messaging.readTimestamps;
};

const selectAllConversationPreviews = createSelector(
	[
		selectAllMessagesAndFiles,
		selectAllConversations,
		selectUnarchivedContactEntities,
		workspaceInfoSelector,
		readTimestampsSelector,
		selectAllSelfAccountIds,
	],
	(messages, conversations, contacts, workspaceInfo, readTimestamps, selfAccountIds) => {
		return conversations
			.map((conversation) => {
				if (isWorkspaceConversation(conversation) && conversation.workspaceId) {
					const workspaceMessages = messages.filter(
						(message) => message.workspaceId === conversation.workspaceId,
					);
					const readTimestamp = readTimestamps.workspaces[conversation.workspaceId];
					return {
						...conversation,
						contact: workspaceInfo[conversation.workspaceId],
						mostRecentMessage: workspaceMessages.lastOrUndefined(),
						readTimestamp,
						numberOfUnreadMessages: countUnreadMessages(
							workspaceMessages,
							readTimestamp,
							selfAccountIds,
						),
					} as ConversationPreview;
				} else if (!isWorkspaceConversation(conversation)) {
					const conv = conversation as IConversationBaseEntity;
					const contact = contacts[conv.participantId!];
					const conversationMessages = messages.filter(
						(message) => !message.workspaceId && message.conversationId === conv.id,
					);
					const readTimestamp = readTimestamps.conversations[conv.id];

					return {
						...conv,
						contact: contact,
						mostRecentMessage: conversationMessages.lastOrUndefined(),
						readTimestamp,
						numberOfUnreadMessages: countUnreadMessages(
							conversationMessages,
							readTimestamp,
							selfAccountIds,
						),
					} as ConversationPreview;
				}
				return undefined;
			})
			.filter((conv) => conv !== undefined) as ConversationPreview[];
	},
);

export const sortConversationsDesc = (() => {
	let lastSorted: ConversationPreview[] = [];
	return createSelector([selectAllConversationPreviews], (previews) => {
		const sorted = [...previews].sort((a, b) => {
			const aMessageHasTimestamp = messageHasTimestamp(a.mostRecentMessage);
			const bMessageHasTimestamp = messageHasTimestamp(b.mostRecentMessage);

			if (!aMessageHasTimestamp && bMessageHasTimestamp) return 1;
			if (aMessageHasTimestamp && !bMessageHasTimestamp) return -1;
			if (!aMessageHasTimestamp && !bMessageHasTimestamp) return a.id.localeCompare(b.id);

			const aSeconds = getMessageTimestamp(a.mostRecentMessage!)?.seconds;
			const bSeconds = getMessageTimestamp(b.mostRecentMessage!)?.seconds;
			return bSeconds - aSeconds;
		});
		if (
			sorted.length === lastSorted.length &&
			sorted.every(
				(conv, i) =>
					(conv.workspaceId ?? conv.id) ===
						(lastSorted[i].workspaceId ?? lastSorted[i].id) &&
					conv.mostRecentMessage?.id === lastSorted[i].mostRecentMessage?.id &&
					// Message & file ids can collide across sequences; the type check
					// disambiguates a preview that swaps between a message and a file.
					(conv.mostRecentMessage && isFile(conv.mostRecentMessage)) ===
						(lastSorted[i].mostRecentMessage &&
							isFile(lastSorted[i].mostRecentMessage!)) &&
					(conv.mostRecentMessage &&
						getMessageTimestamp(conv.mostRecentMessage)?.seconds) ===
						(lastSorted[i].mostRecentMessage &&
							getMessageTimestamp(lastSorted[i].mostRecentMessage!)?.seconds) &&
					conv.numberOfUnreadMessages === lastSorted[i].numberOfUnreadMessages &&
					conv.readTimestamp?.seconds === lastSorted[i].readTimestamp?.seconds &&
					conv.readTimestamp?.nanos === lastSorted[i].readTimestamp?.nanos,
			)
		) {
			return lastSorted;
		}
		lastSorted = sorted;
		return sorted;
	});
})();

export const selectHasConversations = createSelector(
	[selectAllConversationPreviews],
	(previews) => previews.length > 0,
);

export const selectMessagesByConversationKey = createSelector(
	[selectAllMessagesAndFiles, selectConversationByKey],
	(messages, conversation) => {
		if (isWorkspaceConversation(conversation) && conversation?.workspaceId) {
			return messages.filter((message) => message.workspaceId === conversation.workspaceId);
		} else if (conversation?.id) {
			return messages.filter((message) => message.conversationId === conversation.id);
		}
		throw Error("A valid conversation key must be provided");
	},
);

export const selectMessagesByConversationKeyGroupedByDate = createSelector(
	selectMessagesByConversationKey,
	(messages) => {
		return groupBy(messages, (message) =>
			moment(Date.DateFromTimestamp(getMessageTimestamp(message)))
				.startOf("day")
				.unix(),
		);
	},
);

export const selectConversationMessages = createCachedSelector(
	selectAllMessagesAndFiles,
	selectConversationByKey,
	(messages, conversation) => {
		if (conversation?.id === undefined) return;

		const conversationMessages = groupBy(messages, "conversationId")[conversation.id];
		return conversationMessages;
	},
)((_state_, conversationId) => conversationId);

export const selectWorkspaceMessages = createCachedSelector(
	selectAllMessagesAndFiles,
	selectConversationByKey,
	(messages, conversation) => {
		if (conversation?.workspaceId === undefined) return;

		return groupBy(messages, "workspaceId")[conversation.workspaceId];
	},
)((_state_, workspaceId) => workspaceId);

export const selectConversationFiles = createCachedSelector(
	selectAllFiles,
	selectConversationByKey,
	(files, conversation) => {
		if (conversation?.workspaceId?.isNotEmpty()) {
			return groupBy(files, "workspaceId")[conversation.workspaceId];
		} else if (conversation?.id?.isNotEmpty()) {
			return groupBy(files, "conversationId")[conversation.id];
		}

		return undefined;
	},
)((_state_, workspaceId) => workspaceId);

export const selectConversationFilesSortedDesc = createSelector(
	selectConversationFiles,
	(files) => {
		if (!files) return undefined;
		return [...files].sort(
			(a, b) => (b.uploadedTimestamp?.seconds ?? 0) - (a.uploadedTimestamp?.seconds ?? 0),
		);
	},
);

export const selectSelfAccountId = createSelector(
	[selectAllUserContacts, selectActiveIdentityId, selectConversationByKey],
	(contacts, myIdentityId, conversation) => {
		if (conversation?.id === undefined) return undefined;

		if (conversation.participantId?.isNotEmpty()) {
			const target = contacts?.find((contact) => contact.id === conversation.participantId);
			if (target?.workspaceId?.isNotEmpty()) {
				const self = contacts.find(
					(contact) =>
						contact.identityId === myIdentityId &&
						contact.workspaceId === target.workspaceId,
				);
				return self?.id;
			} else {
				const self = contacts.find(
					(contact) =>
						contact.identityId === myIdentityId &&
						contact.workspaceId === PERSONAL_WORKSPACE_ID,
				);
				return self?.id;
			}
		} else if (conversation.workspaceId?.isNotEmpty()) {
			const myAccount = contacts
				.filter((user) => user.workspaceId === conversation.workspaceId)
				.find((user) => user.identityId === myIdentityId);
			return myAccount?.id;
		}
		return;
	},
);

const selectIsConversationsLoaded = (state: MessagingState) => {
	return state.messaging.conversationsLoaded;
};

const selectIsTimestampsLoaded = (state: MessagingState) => {
	if (!Config.firebase.enable) return true;
	return state.messaging.timestampsLoaded;
};

export const selectIsMessagingLoaded = createSelector(
	[selectIsConversationsLoaded, selectIsTimestampsLoaded],
	(conversationsLoaded, timestampsLoaded) => {
		return conversationsLoaded && timestampsLoaded;
	},
);
export const selectSelectedConversationKey = (state: MessagingState) =>
	state.messaging.selectedConversationKey;

export const selectSelectedConversation = createSelector(
	[selectSelectedConversationKey, selectConversationEntities],
	(key, entities) => {
		if (key === undefined) return undefined;

		if (isNewConversation({ id: key })) {
			return {
				id: NEW_CONVERSATION_ID,
				participantId: NEW_CONVERSATION_ID,
			} as IDirectConversationEntity;
		}

		return entities[key];
	},
);

export const selectIsNewConversation = createSelector([selectSelectedConversationKey], (key) => {
	return isNewConversation(key ? { id: key } : undefined);
});

export const selectConversationSidebarView = (state: MessagingState) =>
	state.messaging.conversationSidebarView;

export const selectIsSelf = createSelector(
	[selectActiveIdentityId, selectContactById],
	(identityId, contact) => {
		return contact?.identityId === identityId;
	},
);

export const selectMessagingFilteredWorkspaceId = (state: MessagingState) => {
	return state.messaging.filteredWorkspaceId;
};

export const selectContactIdsFilteredByMessagingWorkspaceId = createSelector(
	[selectMessagingFilteredWorkspaceId, selectAllUserContacts],
	(filteredWorkspaceId, contacts) => {
		return contacts
			.filter((contact) => contact.workspaceId === filteredWorkspaceId)
			.mapNotNull((contact) => contact.id);
	},
);

/**
 * Memoized selector for the contact displayed in ChatView.
 * Uses deep equality on inputs to avoid recomputation when selectors return
 * new references with identical data (e.g. selectUnarchivedContactEntities
 * recreating objects, or selectSelectedConversation returning a new entity ref).
 */
export const selectChatViewContact = createDeepEqualSelector(
	[selectSelectedConversation, selectUnarchivedContactEntities, workspaceInfoSelector],
	(conversation, contactEntities, workspaceInfo): IContactForDisplay | undefined => {
		if (!conversation || isNewConversation(conversation)) return undefined;

		if (conversation.participantId) {
			return contactEntities[conversation.participantId];
		}

		if (conversation.workspaceId) {
			return workspaceInfo[conversation.workspaceId];
		}

		return undefined;
	},
);

const selectWorkspaceMostRecentMessageMap = createSelector(
	[selectAllConversationPreviews],
	(previews) => {
		const map: Record<string, MessageOrFile | undefined> = {};
		for (const preview of previews) {
			if (preview.workspaceId) {
				map[preview.workspaceId] = preview.mostRecentMessage;
			}
		}
		return map;
	},
);

export const selectMostRecentMessageByWorkspaceId = createCachedSelector(
	selectWorkspaceMostRecentMessageMap,
	(_state: RootState, workspaceId: string) => workspaceId,
	(map, workspaceId) => map[workspaceId],
)((_state, workspaceId) => workspaceId);

// Simple path selectors — avoids creating new inline functions on every render
export const selectSelectedMessage = (state: RootState) => state.messaging.selectedMessage;
export const selectRecipient = (state: RootState) => state.messaging.recipient;

// Cached selector for read timestamps, keyed by conversation id + workspaceId
const selectReadTimestamps = (state: RootState) => state.messaging.readTimestamps;
export const selectReadTimestampForConversation = createCachedSelector(
	selectReadTimestamps,
	(_state: RootState, id: string | undefined, _workspaceId?: string) => id,
	(_state: RootState, _id: string | undefined, workspaceId?: string) => workspaceId,
	(readTimestamps, id, workspaceId) => {
		if (id) return readTimestamps.conversations[id];
		if (workspaceId) return readTimestamps.workspaces[workspaceId];
		return undefined;
	},
)((_state, id, workspaceId) => `${id ?? ""}|${workspaceId ?? ""}`);

export const selectIsMessageVoiceFilesHidden = (state: RootState) =>
	state.messaging.voiceFilesHidden;
